import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Film, Radio, Send, Check, Clock, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const ROLES = [
  {
    key: "content_owner",
    label: "Content Owner",
    Icon: Film,
    color: "violet",
    sub: "Upload your own films & series to the catalog. Get analytics on plays, watch-time, completion, ratings.",
  },
  {
    key: "channel_owner",
    label: "Channel Owner",
    Icon: Radio,
    color: "cyan",
    sub: "Run your own GCR Theaters channel. Schedule shows, monetize with ticketed premieres, ad banners.",
  },
];

export default function CreatorApply() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [role, setRole] = useState("content_owner");
  const [form, setForm] = useState({
    name: user?.name || "",
    bio: "",
    portfolio_url: "",
    sample_links: "",
    motivation: "",
  });
  const [mine, setMine] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/creator/applications/mine").then(r => setMine(r.data || [])).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/creator/apply", { role, ...form });
      if (data.already_granted) {
        toast.success("You already have that role — opening your dashboard.");
        nav("/dashboard");
        return;
      }
      toast.success(data.status === "pending" ? "Application submitted! We'll review it shortly." : "Application updated");
      const m = await api.get("/creator/applications/mine");
      setMine(m.data || []);
      setForm({ ...form, motivation: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  };

  const pendingForRole = mine.find((m) => m.role === role && m.status === "pending");

  const accents = {
    violet: "border-violet-500/40 bg-violet-500/5 text-violet-300",
    cyan: "border-cyan-500/40 bg-cyan-500/5 text-cyan-300",
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />
      <main className="pt-28 px-6 lg:px-12 max-w-3xl mx-auto pb-20" data-testid="creator-apply-page">
        <div className="text-xs uppercase tracking-[0.3em] text-[#f6c34a] mb-2">Become a creator</div>
        <h1 className="text-4xl md:text-5xl font-black mb-3" data-testid="creator-apply-heading">Apply to publish on FK Cinema</h1>
        <p className="text-gray-400 mb-10 leading-relaxed">
          Pick a track. We review every application by hand — usually within 24-48 hours.
          Once approved, you unlock your creator dashboard with audience analytics and revenue tools.
        </p>

        {/* Role picker */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {ROLES.map(({ key, label, Icon, color, sub }) => (
            <button
              key={key}
              type="button"
              data-testid={`role-${key}`}
              onClick={() => setRole(key)}
              className={`text-left rounded-xl border-2 p-5 transition ${
                role === key
                  ? `border-[#f6c34a]/70 bg-[#f6c34a]/5`
                  : `border-white/10 bg-[#0e0e0e] hover:border-white/30`
              }`}
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 border ${accents[color]}`}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="font-bold text-lg">{label}</div>
              <div className="text-sm text-gray-400 mt-1 leading-relaxed">{sub}</div>
              {role === key && (
                <div className="mt-3 text-[10px] uppercase tracking-widest text-[#f6c34a] font-bold flex items-center gap-1">
                  <Check className="w-3 h-3" /> Selected
                </div>
              )}
            </button>
          ))}
        </div>

        {pendingForRole && (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4 mb-6 flex items-start gap-3">
            <Clock className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-amber-300">Application pending</div>
              <div className="text-xs text-amber-200/70 mt-1">
                You already have a pending {ROLES.find(r => r.key === role)?.label} application from{" "}
                {new Date(pendingForRole.created_at).toLocaleDateString()}. Re-submitting will just update your details.
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs text-gray-500 uppercase tracking-widest">Display name</label>
            <input
              data-testid="creator-form-name"
              required
              className="input-dark"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="As it should appear publicly"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 uppercase tracking-widest">Bio</label>
            <textarea
              data-testid="creator-form-bio"
              rows="3"
              className="input-dark"
              value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
              placeholder="A short bio (1-2 paragraphs) about you and your work"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 uppercase tracking-widest">Portfolio / website</label>
            <input
              data-testid="creator-form-portfolio"
              className="input-dark"
              value={form.portfolio_url}
              onChange={(e) => setForm({ ...form, portfolio_url: e.target.value })}
              placeholder="https://"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 uppercase tracking-widest">Sample work (YouTube / Vimeo links, one per line)</label>
            <textarea
              data-testid="creator-form-samples"
              rows="3"
              className="input-dark"
              value={form.sample_links}
              onChange={(e) => setForm({ ...form, sample_links: e.target.value })}
              placeholder="https://youtube.com/watch?v=…"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 uppercase tracking-widest">Why should we approve you? *</label>
            <textarea
              data-testid="creator-form-motivation"
              required
              rows="4"
              className="input-dark"
              value={form.motivation}
              onChange={(e) => setForm({ ...form, motivation: e.target.value })}
              placeholder="Tell us about your project, audience, and what you want to publish on FK Cinema."
              minLength={10}
            />
          </div>

          <div className="flex justify-end pt-2">
            <button
              data-testid="creator-form-submit"
              type="submit"
              disabled={submitting}
              className="btn-red px-6 py-3 rounded font-semibold flex items-center gap-2"
            >
              {submitting ? "Submitting…" : (<><Send className="w-4 h-4" /> Submit application</>)}
            </button>
          </div>
        </form>

        {/* My past applications */}
        {mine.length > 0 && (
          <div className="mt-14">
            <h2 className="text-lg font-bold mb-4">My applications</h2>
            <div className="space-y-2">
              {mine.map((a) => (
                <div
                  key={a.application_id}
                  data-testid={`my-application-${a.application_id}`}
                  className="flex items-center justify-between rounded-lg border border-white/10 bg-[#0e0e0e] px-4 py-3"
                >
                  <div>
                    <div className="font-semibold text-sm">{a.role === "channel_owner" ? "Channel Owner" : "Content Owner"}</div>
                    <div className="text-xs text-gray-500">{new Date(a.created_at).toLocaleDateString()}</div>
                  </div>
                  <span className={
                    a.status === "approved" ? "text-green-400 text-xs font-bold uppercase tracking-widest" :
                    a.status === "rejected" ? "text-red-400 text-xs font-bold uppercase tracking-widest" :
                    "text-amber-400 text-xs font-bold uppercase tracking-widest"
                  }>{a.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
